import {
	Box,
	Grid,
	Card,
	CardContent,
	Typography,
	Button,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Alert,
	Snackbar,
	Chip,
	Stack,
	MenuItem,
	IconButton,
} from '@mui/material';
import {
	FileText,
	CheckCircle,
	XCircle,
	AlertCircle,
} from 'lucide-react';
import React, { useEffect, useState, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { GetBusinessInformation, GetClientDocuments, GetClientDocumentsTypes, ReuploadDocuments, UpdateDocuments } from "../../../services/client.services";
import { useParams } from "react-router-dom";
import { LoadingBox } from "../../../components/Box";
import { StyledSelectTextBox } from '../../../components/TextBox';
import { ContainedErrorButton, ContainedSearchButton, OutlinedButton } from '../../../components/Buttons';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { fileValidation } from '../../../utils/utilityFunctions';

export default function UploadAndSign() {
	const { id } = useParams();
	const [clientDocuments, setClientDocuments] = useState(null);
	const [clientDocumentsTypes, setClientDocumentsTypes] = useState(null);
	const [businessId, setBusinessId] = useState(null);
	const [documentType, setDocumentType] = useState('1');
	const [isLoading, setIsLoading] = useState(false);
	const hasRun = useRef(false);
	const [selectedDoc, setSelectedDoc] = useState(null);
	const [reviewNote, setReviewNote] = useState('');
	const [reviewDialog, setReviewDialog] = useState(false);
	const [previewDialog, setPreviewDialog] = useState(false);
	const [blobType, setBlobType] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
	const [files, setFiles] = useState(null);

	const fetchData = async () => {
		setIsLoading(false);
		try {
			await GetBusinessInformation(id).then((data) => {
				setIsLoading(true);
				setBusinessId(data.id);
				GetClientDocumentsTypes(data.id).then((data) => {
					setClientDocumentsTypes(data);
				});
			});
			setIsLoading(false);
		} catch (error) {
			throw error;
		}
	};

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		fetchData();
		// eslint-disable-next-line
	}, [id]);

	if (isLoading || !clientDocumentsTypes) return (
		<LoadingBox>
			<CircularProgress />
		</LoadingBox>
	);

	const handleFetchDocument = (value) => {
		setFiles(null)
		setDocumentType(value)
		GetClientDocuments(businessId, value).then((data) => {
			setBlobType(data.type)
			setClientDocuments(URL.createObjectURL(data));
		});
	}
	const handleReviewSubmit = async (status, note) => {
		try {
			setIsLoading(true);
			const payload = {
				status,
				note
			}
			await UpdateDocuments(payload, businessId, documentType).then((data) => {
				setSnackbar({
					open: true,
					message: `Document ${status === 'APPROVED' ? 'approved' : 'rejected'} successfully`,
					severity: 'success'
				});
				fetchData();
			});

			setReviewDialog(false);
			setReviewNote('');
		} catch (error) {
			setSnackbar({
				open: true,
				message: 'Failed to update document status',
				severity: 'error'
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleFileChange = async (event) => {
		const uploadedFiles = event.target.files[0];

		const error = fileValidation(uploadedFiles);
		if (error) {
			setSnackbar({ open: true, message: `${uploadedFiles?.name}: ${error}`, severity: 'error' });
		} else {
			setFiles(uploadedFiles);
		}
	};

	const handleUpload = async () => {
		try {
			ReuploadDocuments(files, businessId, documentType).then(() => {
					setSnackbar({
						open: true,
						message: `Document Updated successfully`,
						severity: 'success'
					});
					fetchData();
					handleFetchDocument(documentType);
			}).catch(error => {
				setSnackbar({ open: true, message: error.message, severity: "error" });
			});
		} catch (error) {
			setSnackbar({ open: true, message: "Error uploading documents", severity: "error" });
			throw error;
		}

	};


	const getStatusChip = (status) => {
		switch (status) {
			case 'APPROVED':
				return (
					<Chip
						icon={<CheckCircle size={16} />}
						label="Approved"
						color="success"
						size="small"
					/>
				);
			case 'REJECTED':
				return (
					<Chip
						icon={<XCircle size={16} />}
						label="Rejected"
						color="error"
						size="small"
					/>
				);
			default:
				return (
					<Chip
						icon={<AlertCircle size={16} />}
						label="Pending Review"
						color="warning"
						size="small"
					/>
				);
		}
	};

	const DocumentStatus = clientDocumentsTypes?.filter(row => row.category === documentType)[0]?.documentApprovalStatus;

	return (
		<Box>
			<Stack
				direction="row"
				alignItems="center"
				spacing={2}
			>
				<Typography variant="body2" color="neutral.Gray_Dark">
					Select Document:
				</Typography>
				<StyledSelectTextBox
					select
					value={documentType}
					onChange={(event) => handleFetchDocument(event.target.value)}
				>
					<MenuItem value="1" disabled selected>
						-- Select a document type --
					</MenuItem>
					{clientDocumentsTypes.sort((a, b) => a.category.localeCompare(b.category)).map((row, index) => {
						return (
							<MenuItem key={index} value={row.category}>
								{row.category}
							</MenuItem>
						);
					})}
				</StyledSelectTextBox>
			</Stack>

			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Card>
						<CardContent>
							<Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
								<FileText size={24} style={{ marginRight: 12 }} />
								<Box sx={{ flex: 1 }}>
									{clientDocuments ? (
										<>

											<Box sx={{ mb: 2 }}>
												{getStatusChip(DocumentStatus)}
											</Box>
											{DocumentStatus === 'REJECTED' ? (<>
												<Stack direction="row" spacing={1} alignItems="center">
													<Typography variant="body2">Re-upload Document</Typography>
													<IconButton component="label">
														<FileUploadIcon fontSize="large" />
														<input type="file" hidden onChange={(e) => handleFileChange(e)} />
													</IconButton>
												</Stack>

												<Typography variant="body2" sx={{ color: '#093BA7' }}>{files ? files?.name : ''}</Typography>
												<br></br>
											</>) : ''}

											<Box sx={{ mb: 2 }}>
												{blobType === 'application/pdf' ? (
													<iframe
														src={clientDocuments}
														title="PDF Viewer"
														width="100%"
														height="600px"
														style={{ border: 'none' }}
													/>
												) : (
													<img
														src={clientDocuments}
														alt={documentType}
														style={{
															width: '100%',
															height: 200,
															objectFit: 'cover',
															borderRadius: 8,
															cursor: 'pointer'
														}}
														onClick={() => {
															setSelectedDoc({
																documentType,
															});
															setPreviewDialog(true);
														}}
													/>
												)}

											</Box>

											<Box sx={{ display: 'flex', gap: 1 }}>
												<OutlinedButton
													variant="outlined"
													size="small"
													startIcon={<DownloadIcon size={16} />}
													onClick={() => {
														const link = document.createElement("a");
														link.href = clientDocuments;
														link.download = documentType;
														link.click();
													}}
												>
													Download
												</OutlinedButton>
												{DocumentStatus === 'REJECTED' ? 
												<ContainedSearchButton size="small" variant="contained" onClick={handleUpload} disabled={!files ? true: false}>
													Upload Document
												</ContainedSearchButton> : ''}


												{DocumentStatus === 'PENDING' || DocumentStatus === null ? (
													<>
														<ContainedSearchButton
															variant="contained"
															size="small"
															startIcon={<CheckCircle size={16} />}
															onClick={() => handleReviewSubmit('APPROVED', '')}
														>
															Approve
														</ContainedSearchButton>
														<ContainedErrorButton
															variant="contained"
															size="small"
															startIcon={<XCircle size={16} />}
															onClick={() => {
																setReviewDialog(true);
															}}
														>
															Reject
														</ContainedErrorButton>
													</>
												) : ''}

											</Box>
										</>
									) : (
										<Typography color="text.secondary">
											No document type selected
										</Typography>
									)}
								</Box>
							</Box>
						</CardContent>
					</Card>
				</Grid>

			</Grid>
			<br></br>
			{/* Document Review Dialog */}
			<Dialog
				open={reviewDialog}
				onClose={() => setReviewDialog(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					Review {selectedDoc?.title}
				</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						label="Review Notes"
						multiline
						rows={4}
						value={reviewNote}
						onChange={(e) => setReviewNote(e.target.value)}
						sx={{ mt: 2 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setReviewDialog(false)}
						color="inherit"
					>
						Cancel
					</Button>
					<ContainedErrorButton
						onClick={() => handleReviewSubmit('REJECTED', reviewNote)}
						color="error"
						variant="contained"
						startIcon={<XCircle size={16} />}
					>
						Reject
					</ContainedErrorButton>
				</DialogActions>
			</Dialog>

			{/* Document Preview Dialog */}
			<Dialog
				open={previewDialog}
				onClose={() => setPreviewDialog(false)}
				maxWidth="md"
				fullWidth
			>
				<DialogTitle>
					{selectedDoc?.title}
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							p: 2
						}}
					>
						<img
							src={clientDocuments}
							alt={documentType}
							style={{
								maxWidth: '100%',
								maxHeight: '70vh',
								objectFit: 'contain'
							}}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setPreviewDialog(false)}
						color="primary"
					>
						Close
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}
